/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import { Avatar, IconButton } from '@mui/material'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Table, { TableProps } from 'components/Table'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import * as teamMembersClasses from 'pagesNew/TeamMembers/TeamMembersPlatformList/styles'
import { findAllDentalSchools_findAllDentalSchool_items as DentalSchool } from '__generatedTypes__/findAllDentalSchools'
import { FormValues } from 'pagesNew/OrginizeBlogPages/OrganizeCreateDentalSchoolStatistic/Form/types'
import * as classes from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form/SuccessStoriesFieldArray/styles'
import { DentalSchoolsSearch } from '../DentalSchoolsSearch'

export type Props = {
  platform: Platform
  isOptometry: boolean
  onChange: (name: keyof FormValues, value?: DentalSchool[]) => void
  fields: FieldArrayRenderProps<DentalSchool, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<DentalSchool, HTMLElement>['meta']
  linkParams?: URLSearchParams
}

export const DentalSchoolsFieldArray: FC<Props> = ({ platform, isOptometry, fields, meta, onChange, linkParams }) => {
  const { getLink } = useLink()
  const fieldArray = fields.value ?? []

  const handleSort = (newFieldArray: DentalSchool[]) => {
    onChange('schools', newFieldArray)
  }

  const handleAdd = (dentalSchool: DentalSchool) => {
    fields.push(dentalSchool)
  }

  const DentalSchoolsColumns: TableProps<DentalSchool>['columns'] = [
    {
      renderCell: (_dentalSchool, index) => (
        <Typography variant="body2" css={classes.rowIndex}>
          {index + 1}
        </Typography>
      ),
      flex: '0 1 30px',
    },
    {
      renderCell: (dentalSchool) => (
        <div css={classes.avatarWrapper}>
          <Avatar src={dentalSchool.getCoverImage ?? undefined} alt="Photo" />
        </div>
      ),
      flex: '0 1 30px',
    },
    {
      renderCell: (dentalSchool) => (
        <Typography variant="body2" css={[teamMembersClasses.longText, classes.longText]}>
          {dentalSchool.title}
        </Typography>
      ),
    },
    {
      renderCell: (dentalSchool) => (
        <Link
          to={getLink(
            isOptometry ? cmsRoutes.EDIT_OPTOMETRY_SCHOOL : cmsRoutes.EDIT_DENTAL_SCHOOL,
            { dentalSchoolId: dentalSchool.id },
            linkParams
          )}
        >
          <IconButton css={teamMembersClasses.editIcon}>
            <EditIcon />
          </IconButton>
        </Link>
      ),
      flex: '0 1 29px',
    },
    {
      renderCell: (_dentalSchool, index: number) => (
        <IconButton onClick={() => fields.remove(index)} css={teamMembersClasses.editIcon}>
          <CrossIcon />
        </IconButton>
      ),
      flex: '0 1 29px',
    },
  ]

  return (
    <div css={classes.root}>
      <Typography variant="body2" css={classes.label}>
        Order of {isOptometry ? 'Optometry' : 'Dental'} Schools
      </Typography>

      <div css={classes.search}>
        <DentalSchoolsSearch
          platform={platform}
          isOptometry={isOptometry}
          value={fieldArray}
          onAdd={handleAdd}
          error={meta.touched && meta.error}
          formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
        />
      </div>

      {fieldArray.length ? (
        <Table
          css={[teamMembersClasses.table, classes.table]}
          columns={DentalSchoolsColumns}
          items={fieldArray}
          hideHeader
          isSortable
          onSort={handleSort}
        />
      ) : (
        <Typography variant="body2" css={classes.emptyLabel}>
          No {isOptometry ? 'Optometry' : 'Dental'} Schools added
        </Typography>
      )}
    </div>
  )
}
